<template>
  <div class="mall-cart">
    <div class="mall-cart__shell">
      <header class="mall-cart__head">
        <div>
          <h1>장바구니</h1>
          <p v-if="loaded && !isEmpty">{{ productCount }}종 · {{ itemCount }}개</p>
        </div>
        <div class="mall-cart__head-actions">
          <router-link to="/mall/cart/quote" class="mall-cart__quote-link">
            <AppIcon icon="mdi-file-document-outline" :size="18" />
            견적서
          </router-link>
          <button
            v-if="loaded"
            type="button"
            class="mall-cart__sets-toggle"
            :class="{ 'is-open': showSets }"
            @click="toggleSets"
          >
            <AppIcon icon="mdi-playlist-plus" :size="18" />
            자주 담는 목록
            <em v-if="cartSets.length">{{ cartSets.length }}</em>
          </button>
        </div>
      </header>

      <section v-if="showSets" class="mall-cart__sets">
        <div class="mall-cart__sets-head">
          <h2>자주 담는 목록</h2>
          <p>반복 구매 세트를 저장해 두고 한 번에 담을 수 있습니다.</p>
        </div>

        <form v-if="!isEmpty" class="mall-cart__sets-save" @submit.prevent="onSaveSet">
          <input
            v-model.trim="setName"
            type="text"
            maxlength="60"
            placeholder="예: 월간 소모품 · 본원 기본세트"
            required
          />
          <button type="submit" :disabled="setBusy">현재 장바구니 저장</button>
        </form>

        <p v-if="setMessage" class="mall-cart__sets-msg">{{ setMessage }}</p>

        <ul v-if="cartSets.length" class="mall-cart__sets-list">
          <li v-for="set in cartSets" :key="set.idx">
            <div>
              <strong>{{ set.name }}</strong>
              <span>{{ set.itemCount }}종</span>
            </div>
            <div class="mall-cart__sets-actions">
              <button type="button" :disabled="setBusy" @click="onLoadSet(set.idx, false)">
                추가 담기
              </button>
              <button type="button" :disabled="setBusy" @click="onLoadSet(set.idx, true)">
                이걸로 교체
              </button>
              <button
                type="button"
                class="is-danger"
                :disabled="setBusy"
                @click="onDeleteSet(set.idx)"
              >
                삭제
              </button>
            </div>
          </li>
        </ul>
        <p v-else class="mall-cart__sets-empty">저장된 목록이 없습니다.</p>
      </section>

      <div v-if="!loaded || loading" class="mall-cart__loading" aria-busy="true">
        <span class="mall-cart__spinner" />
        <p>장바구니를 불러오는 중입니다</p>
      </div>

      <div v-else-if="isEmpty" class="mall-cart__empty">
        <span class="mall-cart__empty-icon">
          <AppIcon icon="mdi-cart-outline" :size="40" />
        </span>
        <p>장바구니가 비어 있습니다.</p>
        <em>원하시는 상품을 장바구니에 담아 주세요.</em>
        <router-link to="/mall" class="mall-cart__link">쇼핑계속하기</router-link>
      </div>

      <div v-else class="mall-cart__layout">
        <div class="mall-cart__main">
          <p v-if="hasIssue" class="mall-cart__alert">
            <AppIcon icon="mdi-alert-outline" :size="16" />
            품절·재고 부족·가격 변동이 있는 상품이 있습니다. 주문 전에 확인해 주세요.
          </p>

          <div class="mall-cart__select-bar">
            <label class="mall-cart__check">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="selectAll(($event.target as HTMLInputElement).checked)"
              />
              전체선택
            </label>
            <span class="mall-cart__select-count">
              {{ selectedLines.length }} / {{ lines.length }}개 선택
            </span>
            <div class="mall-cart__select-actions">
              <button
                type="button"
                class="mall-cart__ghost-btn"
                :disabled="!selectedLines.length || busyKey === 'bulk'"
                @click="removeSelected"
              >
                선택삭제
              </button>
              <button
                v-if="issueLines.length"
                type="button"
                class="mall-cart__ghost-btn"
                :disabled="busyKey === 'bulk'"
                @click="removeIssues"
              >
                품절·부족 삭제
              </button>
            </div>
          </div>

          <ul class="mall-cart__list">
            <li
              v-for="line in lines"
              :key="line.key"
              class="mall-cart__item"
              :class="{
                'is-issue': line.soldOut || line.stockShort || line.priceChanged,
                'is-editing': Boolean(optionEditors[line.key]),
              }"
            >
              <label class="mall-cart__check mall-cart__check--item">
                <input
                  type="checkbox"
                  :checked="isSelected(line.key)"
                  @change="toggle(line.key)"
                />
                <span class="mall-cart__sr">선택</span>
              </label>
              <router-link :to="`/mall/${line.productId}`" class="mall-cart__thumb">
                <img v-if="line.product.image" :src="line.product.image" :alt="line.product.name" />
                <span v-else class="mall-cart__thumb-fallback">
                  <AppIcon icon="mdi-image-off-outline" :size="22" />
                </span>
              </router-link>
              <div class="mall-cart__body">
                <div class="mall-cart__body-top">
                  <router-link :to="`/mall/${line.productId}`" class="mall-cart__name">
                    {{ line.product.name }}
                  </router-link>
                  <button
                    type="button"
                    class="mall-cart__remove"
                    aria-label="삭제"
                    :disabled="busyKey === line.key"
                    @click="onRemove(line.key)"
                  >
                    <AppIcon icon="mdi-close" :size="18" />
                  </button>
                </div>

                <div class="mall-cart__meta">
                  <MallOrderPackMark
                    v-if="isOrderPack(line.orderStep) && line.product.mallKind !== 'SALE'"
                    size="inline"
                    :order-step="line.orderStep"
                    :sell-unit="line.product.sellUnit"
                  />
                  <span v-if="line.product.mallKind === 'SALE'" class="mall-cart__unit-label">
                    {{ Math.max(1, line.product.packQty || line.orderStep || 1) }}개 묶음
                  </span>
                  <span v-if="line.product.unit" class="mall-cart__unit-label">{{ line.product.unit }}</span>
                  <em v-if="line.optionName" class="mall-cart__option">
                    옵션 · {{ line.optionName }}
                    <template v-if="line.optionExtra">
                      ({{ line.optionExtra > 0 ? '+' : '' }}{{ formatPrice(line.optionExtra) }}원)
                    </template>
                  </em>
                  <button
                    v-if="!line.soldOut"
                    type="button"
                    class="mall-cart__opt-btn"
                    :class="{ 'is-open': optionEditors[line.key] }"
                    :disabled="busyKey === line.key"
                    @click="toggleOptionEditor(line)"
                  >
                    {{ optionEditors[line.key] ? '옵션 닫기' : '옵션 변경' }}
                  </button>
                </div>

                <div v-if="optionEditors[line.key]" class="mall-cart__opt-panel">
                  <p class="mall-cart__opt-title">옵션 선택</p>
                  <div
                    v-for="group in optionEditors[line.key].groups"
                    :key="`${line.key}-${group.name}`"
                    class="mall-cart__opt-row"
                  >
                    <label :for="`cart-opt-${line.key}-${group.name}`">{{ group.name }}</label>
                    <select
                      :id="`cart-opt-${line.key}-${group.name}`"
                      v-model="optionEditors[line.key].selected[group.name]"
                    >
                      <option v-for="option in group.items" :key="option.name" :value="option.name">
                        {{ option.name }}
                        <template v-if="option.extra">
                          ({{ option.extra > 0 ? '+' : '' }}{{ formatPrice(option.extra) }}원)
                        </template>
                      </option>
                    </select>
                  </div>
                  <div class="mall-cart__opt-actions">
                    <button
                      type="button"
                      class="mall-cart__opt-cancel"
                      :disabled="busyKey === line.key"
                      @click="toggleOptionEditor(line)"
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      class="mall-cart__opt-save"
                      :disabled="busyKey === line.key"
                      @click="saveOption(line.key)"
                    >
                      적용
                    </button>
                  </div>
                </div>

                <p v-if="line.soldOut" class="mall-cart__warn">품절된 상품입니다.</p>
                <p v-else-if="line.stockShort" class="mall-cart__warn">
                  재고가 {{ line.product.stock }}개뿐입니다. 수량을 줄여 주세요.
                </p>
                <p v-else-if="line.priceChanged" class="mall-cart__warn">
                  담을 때 {{ formatPrice(line.snapshotPrice || 0) }}원 → 현재
                  {{ formatPrice(line.product.price) }}원
                </p>
                <div class="mall-cart__row">
                  <QtyStepper
                    class="mall-cart__stepper"
                    :model-value="line.qty"
                    :min="line.minOrder"
                    :max="line.maxOrder"
                    :step="line.orderStep"
                    :unit="sellUnitNoun(line.product.sellUnit)"
                    :carton="line.product.mallKind === 'SALE'"
                    :busy="busyKey === line.key"
                    :disabled="line.soldOut"
                    @update:model-value="onSetQty(line.key, $event)"
                  />
                  <strong>{{ formatPrice(line.lineTotal) }}원</strong>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <aside class="mall-cart__summary">
          <h2>주문 요약</h2>
          <p class="mall-cart__summary-pick">선택상품 {{ selectedLines.length }}종</p>
          <dl>
            <div>
              <dt>상품금액</dt>
              <dd>{{ formatPrice(selectedListSubtotal) }}원</dd>
            </div>
            <div>
              <dt>주문 배송비</dt>
              <dd>{{ selectedShippingLabel }}</dd>
            </div>
          </dl>
          <div class="mall-cart__payable">
            <span>결제예정금액</span>
            <strong>{{ formatPrice(selectedPayable) }}원</strong>
          </div>
          <router-link
            v-if="canCheckoutSelected"
            to="/mall/checkout"
            class="mall-cart__checkout"
            @click="clearBuyNowKeys"
          >
            주문하기
          </router-link>
          <button v-else type="button" class="mall-cart__checkout is-disabled" disabled>
            {{ checkoutDisabledLabel }}
          </button>
          <router-link to="/mall" class="mall-cart__continue">쇼핑계속하기</router-link>
          <p class="mall-cart__hint">{{ shippingNote }}</p>
        </aside>
      </div>
    </div>

    <div v-if="undoMessage" class="mall-cart__undo" role="status">
      <span>{{ undoMessage }}</span>
      <button type="button" @click="undoRemove">실행 취소</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import QtyStepper from '@/components/mall/QtyStepper.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import {
  createCartSet,
  deleteCartSet,
  fetchCartSets,
  loadCartSet,
  type MallCartSet,
} from '@/api/mallCartSet'
import { fetchLiveProduct } from '@/api/mallProduct'
import { useCart, type CartLine } from '@/composables/useCart'
import { useCartSelection } from '@/composables/useCartSelection'
import { useContentSnippets, SNIPPET_FALLBACKS } from '@/composables/useContentSnippets'
import { clearBuyNowKeys } from '@/composables/useBuyNowCheckout'
import { formatPrice, type MallProductOption } from '@/constants/mallProducts'
import { formatShippingFeeAmount, toShippingLine } from '@/utils/mallShipping'
import { calcOrderShippingFromLines, isOrderPack, sellUnitNoun } from '@/utils/mallSell'

const {
  lines,
  isEmpty,
  loading,
  loaded,
  itemCount,
  productCount,
  setQty,
  setOption,
  addItem,
  removeItem,
  reload,
} = useCart()

const { selectedLines, allSelected, isSelected, toggle, selectAll } = useCartSelection()

const { loadSnippets, snippet } = useContentSnippets()
const shippingNote = computed(
  () => snippet('mall.cart.shipping_note') || SNIPPET_FALLBACKS['mall.cart.shipping_note'],
)

const busyKey = ref('')
const undoMessage = ref('')
let undoTimer: ReturnType<typeof setTimeout> | undefined
let lastRemoved:
  | { productId: string; qty: number; optionName?: string; optionExtra?: number }
  | null = null
const showSets = ref(false)
const cartSets = ref<MallCartSet[]>([])
const setName = ref('')
const setBusy = ref(false)
const setMessage = ref('')
const optionCache = new Map<string, MallProductOption[]>()

async function loadSets() {
  try {
    cartSets.value = await fetchCartSets()
  } catch {
    cartSets.value = []
  }
}

async function toggleSets() {
  showSets.value = !showSets.value
  if (showSets.value && !cartSets.value.length) {
    await loadSets()
  }
}

async function onSaveSet() {
  if (setBusy.value || isEmpty.value || !setName.value) return
  setBusy.value = true
  setMessage.value = ''
  try {
    await createCartSet({
      name: setName.value,
      items: lines.value.map((line) => ({
        productId: line.productId,
        qty: line.qty,
        optionName: line.optionName,
        optionExtra: line.optionExtra,
      })),
    })
    setName.value = ''
    setMessage.value = '자주 담는 목록에 저장했습니다.'
    await loadSets()
  } catch (error) {
    setMessage.value = error instanceof Error ? error.message : '저장에 실패했습니다.'
  } finally {
    setBusy.value = false
  }
}

async function onLoadSet(idx: number, replace: boolean) {
  if (setBusy.value) return
  if (replace && !window.confirm('현재 장바구니를 비우고 이 목록으로 바꿀까요?')) return

  setBusy.value = true
  setMessage.value = ''
  try {
    const result = await loadCartSet(idx, replace)
    await reload()
    setMessage.value = result.message || '장바구니에 담았습니다.'
  } catch (error) {
    setMessage.value = error instanceof Error ? error.message : '불러오기에 실패했습니다.'
  } finally {
    setBusy.value = false
  }
}

async function onDeleteSet(idx: number) {
  if (setBusy.value) return
  if (!window.confirm('이 목록을 삭제할까요?')) return
  setBusy.value = true
  try {
    await deleteCartSet(idx)
    await loadSets()
    setMessage.value = '목록을 삭제했습니다.'
  } catch (error) {
    setMessage.value = error instanceof Error ? error.message : '삭제에 실패했습니다.'
  } finally {
    setBusy.value = false
  }
}

onMounted(() => {
  void loadSnippets(['mall.cart.shipping_note'])
  /* 세트가 있을 때만 배지를 보여 주기 위해 조용히 불러 둔다 */
  loadSets()
})

interface OptionEditor {
  groups: Array<{ name: string; items: MallProductOption[] }>
  selected: Record<string, string>
}

const optionEditors = reactive<Record<string, OptionEditor>>({})

const hasIssue = computed(() =>
  lines.value.some((line) => line.soldOut || line.stockShort || line.priceChanged),
)
const issueLines = computed(() =>
  lines.value.filter((line) => line.soldOut || line.stockShort),
)
const selectedSubtotal = computed(() =>
  selectedLines.value.reduce((sum, line) => sum + line.lineTotal, 0),
)
const selectedListSubtotal = computed(() =>
  selectedLines.value.reduce((sum, line) => sum + line.listUnit * line.qty, 0),
)
const selectedShipping = computed(() =>
  calcOrderShippingFromLines(
    selectedLines.value.map((line) => toShippingLine(line.product, line.qty)),
  ),
)
const selectedShippingLabel = computed(() => formatShippingFeeAmount(selectedShipping.value))
const selectedPayable = computed(() => selectedSubtotal.value + selectedShipping.value)
const selectedHasBlocker = computed(() =>
  selectedLines.value.some((line) => line.soldOut || line.stockShort),
)
const canCheckoutSelected = computed(
  () => selectedLines.value.length > 0 && !selectedHasBlocker.value,
)
const checkoutDisabledLabel = computed(() => {
  if (!selectedLines.value.length) return '상품을 선택해 주세요'
  if (selectedHasBlocker.value) return '품절·재고 확인 후 주문'
  return '주문하기'
})

async function removeSelected() {
  if (!selectedLines.value.length || busyKey.value) return
  if (!window.confirm(`선택한 ${selectedLines.value.length}개 상품을 삭제할까요?`)) return
  busyKey.value = 'bulk'
  try {
    const keys = selectedLines.value.map((line) => line.key)
    for (const key of keys) {
      await removeItem(key)
    }
  } finally {
    busyKey.value = ''
  }
}

async function removeIssues() {
  if (!issueLines.value.length || busyKey.value) return
  busyKey.value = 'bulk'
  try {
    const keys = issueLines.value.map((line) => line.key)
    for (const key of keys) {
      await removeItem(key)
    }
  } finally {
    busyKey.value = ''
  }
}

function buildGroups(options: MallProductOption[]) {
  const groups = new Map<string, MallProductOption[]>()
  options.forEach((option) => {
    const groupName = option.group?.trim() || '구성'
    const list = groups.get(groupName) ?? []
    list.push(option)
    groups.set(groupName, list)
  })
  return [...groups.entries()].map(([name, items]) => ({ name, items }))
}

async function loadOptions(productId: string) {
  if (optionCache.has(productId)) {
    return optionCache.get(productId) || []
  }
  const detail = await fetchLiveProduct(productId)
  const options = (detail?.options || []).filter((option) => option.use)
  optionCache.set(productId, options)
  return options
}

async function toggleOptionEditor(line: CartLine) {
  if (optionEditors[line.key]) {
    delete optionEditors[line.key]
    return
  }

  busyKey.value = line.key
  try {
    const options = await loadOptions(line.productId)
    const groups = buildGroups(options)
    if (!groups.length) {
      window.alert('이 상품에는 변경할 옵션이 없습니다.')
      return
    }

    const selected: Record<string, string> = {}
    const parts = String(line.optionName || '')
      .split(' / ')
      .map((part) => part.trim())
      .filter(Boolean)

    groups.forEach((group, index) => {
      const fromLine = parts[index]
      const match = group.items.find((item) => item.name === fromLine)
      selected[group.name] = match?.name || group.items[0]?.name || ''
    })

    optionEditors[line.key] = { groups, selected }
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '옵션을 불러오지 못했습니다.')
  } finally {
    busyKey.value = ''
  }
}

async function saveOption(key: string) {
  const editor = optionEditors[key]
  if (!editor || busyKey.value) return

  const names = editor.groups.map((group) => editor.selected[group.name]).filter(Boolean)
  const extra = editor.groups.reduce((sum, group) => {
    const picked = group.items.find((item) => item.name === editor.selected[group.name])
    return sum + (picked?.extra || 0)
  }, 0)

  busyKey.value = key
  try {
    await setOption(key, { name: names.join(' / '), extra })
    delete optionEditors[key]
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '옵션을 바꾸지 못했습니다.')
  } finally {
    busyKey.value = ''
  }
}

async function onSetQty(key: string, qty: number) {
  if (busyKey.value) return
  busyKey.value = key
  try {
    await setQty(key, qty)
  } finally {
    busyKey.value = ''
  }
}

async function onRemove(key: string) {
  if (busyKey.value) return
  const line = lines.value.find((item) => item.key === key)
  if (!line) return
  busyKey.value = key
  try {
    lastRemoved = {
      productId: line.productId,
      qty: line.qty,
      optionName: line.optionName,
      optionExtra: line.optionExtra,
    }
    await removeItem(key)
    undoMessage.value = '장바구니에서 삭제했습니다.'
    window.clearTimeout(undoTimer)
    undoTimer = setTimeout(() => {
      undoMessage.value = ''
      lastRemoved = null
    }, 5000)
  } finally {
    busyKey.value = ''
  }
}

async function undoRemove() {
  if (!lastRemoved || busyKey.value) return
  const snapshot = lastRemoved
  busyKey.value = 'undo'
  try {
    await addItem(
      snapshot.productId,
      snapshot.qty,
      snapshot.optionName
        ? { name: snapshot.optionName, extra: snapshot.optionExtra || 0 }
        : undefined,
    )
    undoMessage.value = ''
    lastRemoved = null
    window.clearTimeout(undoTimer)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '실행 취소에 실패했습니다.')
  } finally {
    busyKey.value = ''
  }
}
</script>

<style lang="scss" src="./MallCartPage.scss"></style>
